import React from 'react';
import { FieldConfig, useField } from 'formik';
import { ComboboxProps, MultiComboboxProps, TextInputProps, NumberInputProps } from 'pounceV2';
import useEventHandler from 'Hooks/useEventHandler';
import { ModalInputV3, ModalInputProps } from 'Components/fields/ModalInput/ModalInputV3';
import CompoundInputV3 from 'Components/CompoundInput/CompoundInputV3';
import ModalInputTriggerV3 from 'Components/CompoundInput/ModalInputTriggerV3';
import { QuotedTextInputV3 } from 'Components/fields/whitespace';
import { useTheme } from '@mui/material';
import { FieldFilterTypes, FieldFilterOperators } from '../constants';
import { getFilterValueFieldLabelAndPlaceholder } from '../utils';

type FilterValueFieldProps = Omit<FieldConfig, 'label' | 'placeholder'> &
  Partial<TextInputProps> & {
    fieldType?: string;
    currentOperator?: string;
  };
const FilterValueFieldV3 = React.forwardRef<HTMLInputElement, FilterValueFieldProps>(
  function FilterValueField(props: FilterValueFieldProps, ref): React.ReactElement {
    const {
      palette: { componentTokens },
    } = useTheme();
    const {
      fieldType,
      currentOperator,
      disabled,
      // Extract BoxProps and style-related props that conflict with FieldConfig
      /* eslint-disable @typescript-eslint/no-unused-vars */
      ref: _ref,
      size: _size,
      color: _color,
      defaultValue: _defaultValue,
      /* eslint-enable @typescript-eslint/no-unused-vars */
      ...fieldProps
    } = props;
    const [field, meta, { setValue }] = useField(fieldProps);
    const setFieldValue = useEventHandler(setValue);

    const {
      component: Component,
      componentProps,
    }: {
      component: any;
      componentProps:
        | Partial<TextInputProps>
        | Partial<NumberInputProps>
        | Partial<ComboboxProps<boolean>>
        | Partial<MultiComboboxProps<string | number | boolean>>
        | Partial<ModalInputProps<string | number>>
        | Partial<React.ComponentProps<typeof CompoundInputV3.AutoCompleteInput>>
        | Partial<React.ComponentProps<typeof CompoundInputV3.Input>>
        | Partial<React.ComponentProps<typeof CompoundInputV3.NumberInput>>;
    } = React.useMemo(() => {
      const operatorDetails = currentOperator
        ? FieldFilterOperators.find(({ value: operatorValue }) => operatorValue === currentOperator)
        : null;
      const inputType =
        Object.keys(operatorDetails?.inputTypes || {}).length >= 1
          ? operatorDetails.inputTypes[fieldType || 'default']
          : null;
      let component: any;
      let compProps:
        | Partial<TextInputProps>
        | Partial<NumberInputProps>
        | Partial<ComboboxProps<boolean>>
        | Partial<MultiComboboxProps<string | number | boolean>>
        | Partial<ModalInputProps<string | number>>
        | Partial<React.ComponentProps<typeof CompoundInputV3.AutoCompleteInput>>
        | Partial<React.ComponentProps<typeof CompoundInputV3.Input>>
        | Partial<React.ComponentProps<typeof CompoundInputV3.NumberInput>> = {
        invalid: meta.touched && Boolean(meta.error),
        'aria-describedby': meta.touched && Boolean(meta.error) ? `${props.name}-error` : undefined,
        ...getFilterValueFieldLabelAndPlaceholder(inputType),
      };

      switch (inputType) {
        case FieldFilterTypes.INT:
        case FieldFilterTypes.FLOAT:
          // FIXME: Number inputs default to 0 which makes placeholders 'invisible'
          //        (they take up space, but are unreadable), as well as breaking
          //        styling of Downshift'd components (CompoundInput components)
          component = CompoundInputV3.NumberInput;
          compProps = {
            ...compProps,
            step: inputType === FieldFilterTypes.INT ? 1 : 0.1,
            value: Number.isNaN(Number(field.value)) ? 0 : Number(field.value),
          } as Partial<NumberInputProps>;
          break;

        case FieldFilterTypes.BOOL_ARRAY:
          component = CompoundInputV3.AutoCompleteInput;
          compProps = {
            ...compProps,
            allowAdditions: false,
            maxContainerHeight: 48,
          } as unknown;

          if (field.value || field.value === false) {
            compProps.value = field.value?.map
              ? field.value
              : [Boolean(field.value)].map((val: any) => (val === 'false' ? false : Boolean(val)));
          } else {
            compProps.value = [];
          }
          compProps = compProps as React.ComponentProps<typeof CompoundInputV3.AutoCompleteInput>;
          // @ts-ignore
          compProps.onSelect = setFieldValue;
          // @ts-ignore
          compProps.fuzzySearch = true;
          break;

        case FieldFilterTypes.BOOL:
          component = CompoundInputV3.AutoCompleteInput;
          compProps = {
            ...compProps,
            searchable: true,
            items: [true, false],
            value: field.value === 'false' ? false : Boolean(field.value),
          } as unknown;
          compProps = compProps as React.ComponentProps<typeof CompoundInputV3.AutoCompleteInput>;
          // @ts-ignore
          compProps.onSelect = setFieldValue;
          // @ts-ignore
          compProps.fuzzySearch = true;
          break;

        case FieldFilterTypes.INT_ARRAY:
        case FieldFilterTypes.FLOAT_ARRAY:
        case FieldFilterTypes.STRING_ARRAY:
        case FieldFilterTypes.IP_ARRAY:
          component = ModalInputV3;
          compProps = {
            ...compProps,
            // *Combobox onChange does not return an event like Formik's field.onChange expects
            onChange: setFieldValue,
          } as Partial<ModalInputProps<string | number>>;
          if (field.value || field.value === false) {
            compProps.value = field.value?.map ? field.value : [field.value];
          } else {
            compProps.value = [];
          }
          compProps.triggerComponent = ModalInputTriggerV3;
          compProps.minHeight = 'unset';
          compProps.verticalAlign = 'middle';
          compProps.borderColor = componentTokens.inputs.colors.normal.stroke.resting;
          compProps._hover = { borderColor: componentTokens.inputs.colors.normal.stroke.hover };
          switch (inputType) {
            case FieldFilterTypes.STRING_ARRAY:
              compProps.validateAddition = (input: string) => typeof input === 'string';
              compProps.arrayType = FieldFilterTypes.STRING_ARRAY;
              break;
            case FieldFilterTypes.INT_ARRAY:
              compProps.validateAddition = (input: string, existing, splitOnComma) => {
                return splitOnComma
                  ? input
                      .split(',')
                      .every(
                        (numberInput: string) =>
                          !Number.isNaN(Number(numberInput)) &&
                          Number(numberInput) === Math.round(Number(numberInput)),
                      )
                  : !Number.isNaN(Number(input)) && Number(input) === Math.round(Number(input));
              };
              compProps.arrayType = FieldFilterTypes.INT_ARRAY;
              break;
            case FieldFilterTypes.FLOAT_ARRAY:
              compProps.validateAddition = (input: string, existing, splitOnComma) => {
                return splitOnComma
                  ? input
                      .split(',')
                      .every((numberInput: string) => !Number.isNaN(Number(numberInput)))
                  : !Number.isNaN(Number(input));
              };
              compProps.arrayType = FieldFilterTypes.FLOAT_ARRAY;
              break;
            case FieldFilterTypes.IP_ARRAY:
              compProps.validateAddition = (input: string) => typeof input === 'string';
              compProps.arrayType = FieldFilterTypes.IP_ARRAY;
              break;
            default:
              break;
          }
          break;

        case FieldFilterTypes.STRING:
        case FieldFilterTypes.IP:
        case FieldFilterTypes.CIDR:
          component = QuotedTextInputV3;
          compProps = {
            ...compProps,
            value: field.value ? String(field.value) : '',
          } as Partial<TextInputProps>;
          break;

        default:
          break;
      }
      return { component, componentProps: compProps };
    }, [
      fieldType,
      currentOperator,
      field.value,
      props.name,
      meta.error,
      meta.touched,
      setFieldValue,
      componentTokens.inputs.colors.normal.stroke.hover,
      componentTokens.inputs.colors.normal.stroke.resting,
    ]);

    React.useEffect(() => {
      if (currentOperator && !Component) {
        setFieldValue(undefined);
      }
      if (Component && typeof field.value !== typeof componentProps.value) {
        setFieldValue(componentProps.value);
      }
    }, [Component, currentOperator, setFieldValue, field.value, componentProps.value]);

    return Component ? (
      <Component
        autoFocus
        required
        disabled={disabled || !currentOperator}
        ref={ref}
        {...fieldProps}
        {...field}
        {...(componentProps || {})}
      />
    ) : null;
  },
);

export default FilterValueFieldV3;
